import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Flex, message, Table, Row, Col, Tooltip } from 'antd';
import CustomDeleteModal from '../../Modals/CustomDeleteModal';
import {
  CustomParagraph,
  CustomPagination,
  PageHeaderActions,
  CustomShipmentText,
  CommonDrawer,
  TeamBadge,
} from '@/components';
import { useToken } from '../../../common/helper';
import useUserDateTimeFormat from '@/hooks/useUserDateTimeFormat';
import { useSpin } from '@/components/SpinContext.jsx';
import { DeleteIcon, PencilIcon } from '@/assets/icons/icons';
import {
  DeleteNotification,
  GetNotificationById,
  GetNotificationModel,
} from '../../../apis/onboarding';
import { useSelector } from 'react-redux';
import { getPermissions } from '../../../common/helper/getPermissions';
import CreateNotification from './CreateNotification';
import { getNotificationListTriggerType } from '@/common/utils/notificationUtils';
import theme from '@/assets/theme';
import useUnauthorize from '@/hooks/useUnauthorize';
import { useTranslation } from 'react-i18next';
import { useTableScroll } from '@/hooks/useTableScroll';
import './NotificationList.css';

const NotificationList = () => {
  const { t } = useTranslation('settings');
  const location = useLocation();
  const navigate = useNavigate();
  const { unauthorize } = useUnauthorize();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState('All'); // all and  only mine filter for notification
  const [currentNotification, setCurrentNotification] = useState(null);
  const [step, setStep] = useState(1);
  // Opened from the User Activity feed: show a back link to it.
  const [fromActivity, setFromActivity] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [searchInList, setSerachInList] = useState('');
  const { token } = useToken();
  const { formatDateTime } = useUserDateTimeFormat();
  const { showSpin, hideSpin } = useSpin();
  const [notificationList, setNotificationList] = useState([]);
  const main_menu = useSelector((state) => state.menu.items);
  const notificationPermissions = getPermissions(
    main_menu,
    'Settings',
    'Notifications'
  );
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(50);
  const isClientTheme = theme.themeType === 2;
  const teamLabelPlural = t(isClientTheme ? 'clients' : 'teams');
  const transportProviderLabelPlural = t(
    isClientTheme ? 'carriers' : 'transportProviders'
  );
  const tableScroll = useTableScroll();

  //Pagination function for updating page State
  const handlePageChange = (newPage) => {
    setPage(() => {
      const validPage = newPage > 0 ? newPage : 1;
      return validPage;
    });
  };

  //search
  const handleSearch = (data) => {
    setSerachInList(data);
    setNotificationList([]);
    setPage(1);
  };

  const rangeStart = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = totalCount === 0 ? 0 : Math.min(page * pageSize, totalCount);

  const fetchNotifications = useCallback(
    (search = '', page = 1) => {
      if (!search) {
        showSpin();
      }

      let filterBy = selectedItem == 'All' ? 'all' : 'mine';

      GetNotificationModel(token, page, search, filterBy)
        .then((res) => {
          const data = res?.data ? res.data : null;
          if (data) {
            if (data?.data?.is_success) {
              const defaultTeamId = 'bcea9036-0492-4c64-8c56-cae364162866';
              let res = Array.isArray(data?.data?.data) ? data.data.data : [];
              res = res.map((item) => ({
                ...item,
                teams:
                  item.teams?.filter((teamId) => teamId !== defaultTeamId) ||
                  [],
              }));

              setNotificationList(res);
              setTotalPages(data?.data?.page_count);
              setTotalCount(data?.data?.total_count);
            } else {
              message.error(data?.data?.message);
            }
          }
        })
        .catch((error) => {
          unauthorize(error);
        })
        .finally(() => {
          hideSpin();
        });
    },
    [hideSpin, showSpin, token, unauthorize, selectedItem]
  );
  const handleEdit = (record) => {
    setFromActivity(false);
    setCurrentNotification(record);
    setDrawerOpen(true);
  };

  const handleDeleteClick = (notification) => {
    setSelectedNotification(notification);
    setDeleteModalVisible(true);
  };

  const handleConfirmDelete = () => {
    showSpin();
    DeleteNotification(selectedNotification?.id, token)
      .then((res) => {
        const data = res?.data || null;
        if (data) {
          if (data.data?.is_success) {
            message.success(data?.data?.message);
            fetchNotifications('', 1);
          } else {
            message.error(data?.data?.message);
          }
        }
      })
      .catch((error) => {
        unauthorize(error);
      })
      .finally(() => {
        hideSpin();
      });

    setDeleteModalVisible(false);
    setSelectedNotification(null);
  };

  const handleAddNotification = () => {
    setFromActivity(false);
    setCurrentNotification(null);
    setDrawerOpen(true);
  };

  const columns = [
    {
      title: 'Trigger type',
      dataIndex: 'title',
      width: '22%',
      render: (_, record) => {
        const { title, formatted, tooltipList, moreText } =
          getNotificationListTriggerType(record);
        return (
          <>
            <div className='notification-list-title-row'>
              {record?.alert_type === 'CRITICAL_ALERT' ||
              record?.alert_type === 'PICKUP_DELIVERY' ? (
                <span className='font-12 fw-400'>
                  {' '}
                  {formatted ? `${title}:${formatted}` : `${title}`}
                  {moreText && (
                    <Tooltip
                      title={tooltipList}
                      classNames={{ root: 'font-12' }}
                    >
                      <span className='c-pointer'>{moreText}</span>
                    </Tooltip>
                  )}
                </span>
              ) : (
                <CustomShipmentText
                  text={
                    record?.alert_type == 'LOW_INVENTORY'
                      ? 'Inventory'
                      : record?.title || '—'
                  }
                  fontSize={14}
                />
              )}
            </div>
            <p className='zl-para-10 gray-5e'>{record?.sub_title || '—'}</p>
          </>
        );
      },
    },
    {
      title: 'Shipment filters',
      dataIndex: 'filters',
      width: '19%',
      render: (_value, record) => {
        const mapping = [
          {
            key: 'filter_origins',
            type: 3,
            name: 'Origin',
            boolKey: 'select_all_origins',
          },
          {
            key: 'filter_destinations',
            type: 4,
            name: 'Destination',
            boolKey: 'select_all_destinations',
          },
          {
            key: 'filter_teams',
            type: 5,
            name: teamLabelPlural,
            boolKey: 'select_all_teams',
          },
          {
            key: 'filter_transport_providers',
            type: 2,
            name: transportProviderLabelPlural,
            boolKey: 'select_all_transport_providers',
          },
          {
            key: 'products',
            type: 1,
            name: 'Products',
            boolKey: 'select_all_products',
          },
        ];
        const types = mapping
          .filter(({ key }) => record?.[key]?.length > 0)
          .map(({ key, name, boolKey }) => {
            const items = record[key] || [];
            const boolValue = record[boolKey];
            return {
              name,
              count: items.length,
              boolValue,
              preview: record[boolKey]
                ? 'All'
                : items
                    .map((item) => item?.name || item?.title)
                    .slice(0, 6)
                    .join(', '),
            };
          });

        if (types.length === 0) {
          return <span>-</span>;
        }

        // If all types have boolValue true, show 'All' once
        if (
          types.every((x) => x.boolValue) &&
          types?.length === mapping?.length
        ) {
          return (
            <Tooltip
              classNames={{ root: 'font-12' }}
              title={
                <div>
                  {types.map((t) => (
                    <div
                      key={t.name}
                      className='notification-list-tooltip-row notification-list-tooltip-row--lg'
                    >
                      <strong>
                        {t.name} ({t.count})
                      </strong>
                      <div className='fw-400'>
                        {t.preview}
                        {t.count > 6 && !t.boolValue && ' ...'}
                      </div>
                    </div>
                  ))}
                </div>
              }
            >
              <div className='notification-filter-tag white-tag c-pointer'>
                <span>ALL</span>
              </div>
            </Tooltip>
          );
        }
        const visible = types.slice(0, 2);
        const hidden = types.slice(2);

        return (
          <>
            {visible.map((t) => (
              <Tooltip
                key={t.name}
                classNames={{ root: 'font-12' }}
                title={
                  <div>
                    <div className='notification-list-tooltip-row'>
                      <strong>
                        {t.name} ({t.count})
                      </strong>{' '}
                      <div className='fw-400'>
                        {t.preview}
                        {t.count > 6 && !t.boolValue && ' ...'}
                      </div>
                    </div>
                  </div>
                }
              >
                <div className='notification-filter-tag white-tag c-pointer'>
                  <span>{t.name}</span>
                </div>
              </Tooltip>
            ))}

            {hidden.length > 0 && (
              <Tooltip
                classNames={{ root: 'font-12' }}
                title={
                  <div>
                    {hidden.map((t) => (
                      <div
                        key={t.name}
                        className='notification-list-tooltip-row'
                      >
                        <strong>
                          {t.name} ({t.count})
                        </strong>{' '}
                        <div className='fw-400'>
                          {t.preview}
                          {t.count > 6 && !t.boolValue && ' ...'}
                        </div>
                      </div>
                    ))}
                  </div>
                }
              >
                <div className='notification-filter-tag white-tag c-pointer'>
                  +{hidden.length}
                </div>
              </Tooltip>
            )}
          </>
        );
      },
    },
    {
      title: 'Channel',
      dataIndex: 'notification_method',
      width: '8%',
      render: (_value, record) => (
        <CustomParagraph
          text={record?.notification_method?.toLowerCase() || '—'}
          fontSize={14}
          fontWeight={400}
          color='#333'
          textTransform={'capitalize'}
        />
      ),
    },
    {
      title: 'Recipients',
      key: 'type_delivery',
      width: '18%',
      render: (_, item) => {
        const teamBadge = item?.send_to?.teams_users.find((t) => t.select_all);
        const teamUsers = (
          Array.isArray(item?.send_to?.teams_users)
            ? item.send_to.teams_users
            : []
        ).flatMap((team) => {
          if (team?.select_all) {
            return teamBadge && team?.name === teamBadge?.name
              ? []
              : [team?.name];
          }

          return Array.isArray(team?.users)
            ? team.users
                .filter((u) => u?.enabled !== false)
                .map((u) => u?.name || u?.email || u?.id)
            : [];
        });

        const additionalUsers = Array.isArray(item?.send_to?.additional)
          ? item?.send_to?.additional
          : [];

        const filteredAdditionalUsers = additionalUsers
          ?.filter((obj) => obj?.enabled !== false)
          .map((obj) => obj.label || obj.key);
        const allUsers = [...teamUsers, ...filteredAdditionalUsers];

        // Build JSX content
        const content =
          allUsers.length > 0 || teamBadge ? (
            <>
              {teamBadge ? (
                <TeamBadge
                  color={teamBadge?.color}
                  text={teamBadge?.name}
                  tooltipText={teamBadge?.name}
                  truncateLength={17}
                  className='notification-team-badge'
                />
              ) : (
                allUsers[0]
              )}
              {allUsers.length >= 1 && (
                <>
                  <Tooltip
                    classNames={{ root: 'font-12' }}
                    title={
                      teamBadge
                        ? allUsers.join(', ')
                        : allUsers.slice(1).join(', ')
                    }
                  >
                    <span className='notification-list-user-more'>
                      {teamBadge
                        ? `+${allUsers.length}`
                        : allUsers.length - 1 > 0
                          ? `+${allUsers.length - 1}`
                          : ''}
                    </span>
                  </Tooltip>
                </>
              )}
            </>
          ) : (
            '—'
          );
        return (
          <div
            className={`notification-list-users${
              teamBadge ? ' notification-list-users--flex' : ''
            }`}
          >
            {content}
          </div>
        );
      },
    },
    {
      title: 'Last triggered',
      dataIndex: 'triggered',
      width: '14%',
      render: (_value, record) => (
        <CustomParagraph
          text={formatDateTime(record?.triggered) || '—'}
          fontSize={14}
          fontWeight={400}
          color='#333'
        />
      ),
    },

    {
      title: 'Created by',
      dataIndex: 'created_by_name',
      key: 'created_by_name',
      render: (text) => (
        <CustomParagraph
          text={text || '—'}
          fontSize={14}
          fontWeight={400}
          color='#333'
        />
      ),
    },

    // {
    //   title: 'Last updated',
    //   dataIndex: 'lastUpdated',
    //   width: '15%',
    //   render: (_value, record) => (
    //     <CustomParagraph
    //       text={formatDate(record?.updated_at) || '—'}
    //       fontSize={14}
    //       fontWeight={400}
    //       color='#333'
    //     />
    //   ),
    // },

    {
      title: '',
      key: 'action',
      render: (_, record) => {
        return (
          <Flex align='center' gap={6}>
            {record?.permissions?.is_update && (
              <Flex
                justify='center'
                align='center'
                className='action-icon-wrapper'
                role='button'
                aria-label='Edit'
                onClick={() => handleEdit(record)}
              >
                <PencilIcon color='var(--text-primary)' />
              </Flex>
            )}

            {record?.permissions?.is_delete && (
              <Flex
                justify='center'
                align='center'
                className='action-icon-wrapper'
                role='button'
                aria-label='Delete'
                onClick={() => handleDeleteClick(record)}
              >
                <DeleteIcon />
              </Flex>
            )}
          </Flex>
        );
      },
    },
  ];
  useEffect(() => {
    fetchNotifications(searchInList, page);
  }, [fetchNotifications, page, searchInList]);

  // Deep link from the User Activity feed: open the alert in edit mode.
  const openIdConsumedKeyRef = useRef(null);
  useEffect(() => {
    const alertId = location.state?.__zl_open_notification_id;
    if (!alertId || openIdConsumedKeyRef.current === location.key) return;
    openIdConsumedKeyRef.current = location.key;
    // One-shot: strip it so a refresh doesn't reopen the drawer.
    navigate(location.pathname, { replace: true, state: null });

    showSpin();
    GetNotificationById(alertId, token)
      .then((res) => {
        const data = res?.data?.data;
        if (data?.is_success && data?.data) {
          if (!data.data.permissions?.is_update) {
            message.error(
              "You don't have permission to edit this notification."
            );
            return;
          }
          setCurrentNotification(data?.data);
          setStep(1);
          setFromActivity(true);
          setDrawerOpen(true);
        } else {
          message.error(data?.message || 'Notification not found.');
        }
      })
      .catch((error) => {
        if (error?.status === 404) {
          message.error('Notification not found. It may have been deleted.');
        } else {
          unauthorize(error);
        }
      })
      .finally(() => {
        hideSpin();
      });
  }, [
    location.key,
    location.state,
    location.pathname,
    navigate,
    showSpin,
    hideSpin,
    token,
    unauthorize,
  ]);

  const handleClose = () => {
    setDrawerOpen(false);
    setFromActivity(false);
    fetchNotifications(searchInList, page);
  };

  return (
    <>
      <div className='settings-content-wrapper'>
        <PageHeaderActions
          title={'Notifications'}
          showToggle={false}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          onSearchChange={handleSearch}
          onSearchClear={handleSearch}
          totalCount={totalCount}
          showCount={true}
          showAddButton={notificationPermissions?.is_create}
          onAddClick={handleAddNotification}
          showSearch={true}
          selectedItem={selectedItem}
          setSelectedItem={setSelectedItem}
        ></PageHeaderActions>
        <Table
          columns={columns}
          dataSource={notificationList}
          pagination={false}
          className='settings-table transport-provider-table notificatiolist-table settings-table-action-cols'
          scroll={tableScroll}
        />
        <Row
          style={{
            position: 'relative',
            paddingBottom: '40px',
            marginTop: '16px',
          }}
        >
          <Col className='flex-to-block' xs={24}>
            {notificationList?.length > 0 && (
              <Flex className='pagination-alignment'>
                <CustomPagination
                  maxPageSize={50}
                  page={page}
                  totalPages={totalPages}
                  pageSize={50}
                  onPageChange={handlePageChange}
                />
              </Flex>
            )}
          </Col>
        </Row>
      </div>

      <CustomDeleteModal
        visible={deleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
        onConfirm={handleConfirmDelete}
        title='Remove Notification'
        message='By removing it, you will no longer receive this notification. Other users will not be affected.'
        cancelText='Cancel'
        confirmText='Remove'
        confirmColor='var(--danger)'
      />

      {drawerOpen && (
        <CommonDrawer
          onBack={
            step === 3
              ? () => setStep((prev) => prev - 1)
              : fromActivity
                ? // Restores the activity panel's filters/scroll from history.
                  () => navigate(-1)
                : undefined
          }
          backLabel={step !== 3 && fromActivity ? 'User activity' : undefined}
          open={drawerOpen}
          onClose={() => {
            setDrawerOpen(false);
            setFromActivity(false);
          }}
        >
          <CreateNotification
            selected={currentNotification}
            drawerOpen={drawerOpen}
            step={step}
            setStep={setStep}
            onClose={handleClose}
            fetchNotifications={fetchNotifications}
          />
        </CommonDrawer>
      )}
    </>
  );
};

export default NotificationList;
